/**
 * Centralised profanity filter for ALL free-text fields.
 *
 * Wraps the `bad-words` library and adds two things its default list misses:
 *   1. Extra slurs / casual spelling variants (e.g. "nigga").
 *   2. Leetspeak + obfuscation evasion (e.g. "n1gga", "f@ggot", "n i g g a").
 *
 * Use the shared `profanityFilter` singleton everywhere so the rules stay
 * consistent and can be tightened in one place. NOTE: client-side filtering is
 * a UX guardrail, not a security control — it can be bypassed, so anything that
 * matters should also be checked server-side.
 */

import { Filter } from "bad-words";

// Slurs / variants the default dictionary doesn't cover. Whole-word matched
// (after leetspeak normalisation) so they won't trip up innocent words.
const EXTRA_WORDS = [
  "nigga", "niggas", "niggah", "nigguh", "niglet",
  "tranny", "trannies",
  "wetback", "wetbacks",
];

// The worst slurs, also blocked when fully obfuscated (spaces/punctuation
// stripped). Only includes terms that are NOT substrings of common English
// words, to avoid false positives (e.g. "coon"→raccoon, "spic"→suspicious are
// deliberately excluded from substring matching and rely on whole-word checks).
const HARD_BLOCK_SUBSTRINGS = [
  "nigger", "nigga", "faggot", "kike", "wetback", "tranny", "niglet",
];

// Common leetspeak substitutions mapped back to letters.
const LEET_MAP = {
  "0": "o", "1": "i", "!": "i", "|": "i", "3": "e",
  "4": "a", "@": "a", "5": "s", "$": "s", "7": "t",
  "8": "b", "9": "g",
};
const LEET_RE = /[0134578!@$|9]/g;

const filter = new Filter();
filter.addWords(...EXTRA_WORDS);

// Leetspeak → letters, preserving word boundaries (so whole-word checks work).
function leetNormalize(text) {
  return String(text || "").toLowerCase().replace(LEET_RE, (c) => LEET_MAP[c] || c);
}

// Strip everything but letters → a single token, for catching spaced/dotted
// evasion like "n i g g a" or "n.i.g.g.a".
function flatten(text) {
  return leetNormalize(text).replace(/[^a-z]/g, "");
}

function isProfane(text) {
  if (!text) return false;
  const raw = String(text);

  // 1) Standard whole-word check (default list + EXTRA_WORDS).
  if (safeIsProfane(raw)) return true;

  // 2) Same check after undoing leetspeak ("n1gga" → "nigga").
  if (safeIsProfane(leetNormalize(raw))) return true;

  // 3) Obfuscation-proof substring check for the worst, unambiguous slurs.
  const flat = flatten(raw);
  return HARD_BLOCK_SUBSTRINGS.some((w) => flat.includes(w));
}

function safeIsProfane(text) {
  try {
    return filter.isProfane(text);
  } catch {
    return false;
  }
}

function clean(text) {
  try {
    return filter.clean(String(text || ""));
  } catch {
    return text;
  }
}

function addWords(...words) {
  filter.addWords(...words);
}

export const profanityFilter = { isProfane, clean, addWords };
export default profanityFilter;
